import { GetApiPayloadByUrl, GetApiResponseByUrl } from '@crossbeam/openapi';

import { computed, inject, ref } from 'vue';
import { useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import { ProvidedExportLimitReached } from '@/injectionKeys/global';
import {
  useConnectionsStore,
  useFeedsStore,
  useIntegrationProfileStore,
  usePartnerCloudStore,
} from '@/stores';
import {
  CONNECTION_STATUS_DISPLAY,
  ConnectionStatusType,
} from '@/types/integration_profiles';
import { PCloudIntegration } from '@/types/integrations';

import useAuth from './useAuth';
import useIntegrationProfile from './useIntegrationProfile';

type DatabricksPushStatus = GetApiResponseByUrl<
  '/v0.1/connections/databricks',
  'get'
>;

type DatabricksConfig = NonNullable<
  GetApiPayloadByUrl<'/v0.1/connections/databricks', 'post'>
>;

// Module-level shared state so all composable instances stay in sync
const databricksPushStatus = ref<DatabricksPushStatus | null>(null);

export default function useDatabricks() {
  const feedsStore = useFeedsStore();
  const connectionsStore = useConnectionsStore();
  const partnerCloudStore = usePartnerCloudStore();
  const { hasPermission } = useAuth();

  const databricksConnection = computed(() =>
    connectionsStore.getConnectionByType('databricks'),
  );

  const canManageIntegrations = computed(() =>
    hasPermission('manage:integrations'),
  );

  const isUpdating = ref(false);
  const hasErrors = ref(false);
  const testConnectionError = ref<string | null>(null);
  const testConnectionSuccess = ref(false);

  const exportLimitReached = inject(ProvidedExportLimitReached, null);

  const profileStore = useIntegrationProfileStore();
  const integrationProfileSettings = computed(() =>
    profileStore.getIntegrationProfileByType('databricks'),
  );

  const { isSaving } = useIntegrationProfile('databricks', {
    initOnMount: false,
  });

  const hasDatabricksFeed = computed(
    () => !!feedsStore.getFeedByDataSourceType('databricks'),
  );

  const isPushEnabled = computed(
    () => databricksPushStatus.value?.push_enabled === true,
  );

  async function refreshPushStatus() {
    const { data } = await crossbeamApi.GET('/v0.1/connections/databricks');
    databricksPushStatus.value = data ?? null;
  }

  const connectionStatus = computed<ConnectionStatusType>(() => {
    if (exportLimitReached?.value || hasErrors.value) {
      return 'action_needed';
    }
    if (!databricksPushStatus.value) {
      return 'action_needed';
    }
    if (!isPushEnabled.value) return 'paused';
    if (!integrationProfileSettings.value && !isSaving.value) {
      return 'setting_available';
    }
    return 'active';
  });

  const statusText = computed(
    () => CONNECTION_STATUS_DISPLAY[connectionStatus.value].text,
  );
  const statusType = computed(
    () => CONNECTION_STATUS_DISPLAY[connectionStatus.value].type,
  );

  const calloutText = computed(() => {
    if (exportLimitReached?.value) {
      return 'Reach out to <a target="_blank" href="mailto:support@crossbeam.com" class="text-secondary-text">support@crossbeam.com</a> to increase your export limit';
    }
    if (hasErrors.value) {
      return 'There is an error with the Databricks Custom Object connection. Reach out to <a target="_blank" href="mailto:support@crossbeam.com" class="text-secondary-text">support@crossbeam.com</a> for further assistance';
    }
    if (!isPushEnabled.value) {
      return 'Click the Set Up button to connect to Databricks and start pushing overlaps';
    }
    if (isPushEnabled.value && !integrationProfileSettings.value) {
      return 'All overlaps are currently being pushed to Databricks. Click the Settings button if you would like to configure your push settings.';
    }
    return '';
  });

  const calloutTitle = computed(() => {
    if (exportLimitReached?.value) {
      return 'Export Limit Reached';
    }
    if (hasErrors.value) {
      return 'Databricks Custom Object connection failed';
    }
    if (!isPushEnabled.value) {
      return 'Databricks Custom Object connection not set up';
    }
    if (!integrationProfileSettings.value && !isSaving.value) {
      return 'Databricks Custom Object push settings have not been created';
    }
    return '';
  });

  const integrationApp = computed<PCloudIntegration | undefined>(() =>
    partnerCloudStore.getAvailableIntegrationByType('databricks'),
  );

  const router = useRouter();

  async function testDatabricksConnection(config: DatabricksConfig) {
    testConnectionError.value = null;
    testConnectionSuccess.value = false;
    const { data } = await crossbeamApi.POST(
      '/v0.1/connections/databricks/test',
      { body: config },
    );
    if (data?.success) {
      testConnectionSuccess.value = true;
    } else {
      testConnectionError.value = data?.error ?? 'Connection test failed';
    }
    return data;
  }

  async function enableDatabricksPush(config: DatabricksConfig) {
    isUpdating.value = true;
    try {
      const { data } = await crossbeamApi.POST('/v0.1/connections/databricks', {
        body: config,
      });
      if (data?.success) {
        hasErrors.value = false;
        await Promise.all([
          refreshPushStatus(),
          connectionsStore.refreshConnectionsStore(),
        ]);
        router.push({ name: 'databricks_push_settings' });
      } else {
        hasErrors.value = true;
      }
      return data;
    } catch (_e) {
      hasErrors.value = true;
    } finally {
      isUpdating.value = false;
    }
  }

  async function disableDatabricksPush() {
    isUpdating.value = true;
    try {
      await crossbeamApi.DELETE('/v0.1/connections/databricks');
      await Promise.all([
        refreshPushStatus(),
        connectionsStore.refreshConnectionsStore(),
      ]);
    } finally {
      isUpdating.value = false;
    }
  }

  return {
    canManageIntegrations,
    databricksConnection,
    databricksPushStatus,
    disableDatabricksPush,
    enableDatabricksPush,
    hasDatabricksFeed,
    hasErrors,
    integrationApp,
    integrationProfileSettings,
    isUpdating,
    isPushEnabled,
    connectionStatus,
    statusText,
    statusType,
    calloutText,
    calloutTitle,
    refreshPushStatus,
    testConnectionError,
    testConnectionSuccess,
    testDatabricksConnection,
  };
}
